import Loading from "../../../components/Loading";
import UserNotFound from "../../../components/UserNotFound";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from "react";
import { useUserContext } from "../../../features/users";
import { useBrandContext } from "../../../hooks/useBrandContext";
import { useSubdomain } from "../../../hooks/useSubdomain";
import { TransitionOverlay } from "../../../components/TransitionOverlay";

// Conditional debug logging - only logs in development
const debugLog = (message: string, data?: any) => {
    if (process.env.NODE_ENV === 'development') {
        console.log(message, data);
    }
};

// Helper function to find a project by tag in project memberships
const findProjectInMemberships = (projectMemberships: any[], tag: string) => {
    return projectMemberships.find((membership: any) => membership.project?.tag === tag);
};

// Helper function to find a project by tag in organization memberships
const findProjectInOrganizations = (organizationMemberships: any[], tag: string) => {
    for (const membership of organizationMemberships) {
        const matchingProject = membership.organization?.projects?.find((project: any) => project.tag === tag);
        if (matchingProject) {
            return { project: matchingProject, organization: membership.organization };
        }
    }
    return null;
};

// Helper function to find an organization by tag
const findOrganizationByTag = (organizationMemberships: any[], tag: string) => {
    return organizationMemberships.find((membership: any) => membership.organization?.tag === tag)?.organization;
};

// Helper function to get the appropriate URL for a project
const getProjectUrl = (project: any, projectMembership: any, advisorUuid?: string) => {
    if (projectMembership?.role === "advisor") {
        return `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${project.tag}.${process.env.REACT_APP_APP_ORIGIN}/advisor${advisorUuid ? `/feedback/latest/response` : ""}`;
    } else if (project.experience === "advisor") {
        return `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${project.tag}.${process.env.REACT_APP_APP_ORIGIN}/c${advisorUuid ? `/activity#${advisorUuid}` : ""}`;
    } else {
        return `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://app.${process.env.REACT_APP_APP_ORIGIN}/o/${project?.organization?.tag}/d/${project?.tag}`;
    }
};

// Helper function to get the appropriate URL for an organization
const getOrganizationUrl = (organization: any) => {
    if (organization.experience === "advisor") {
        return `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${organization.tag}.${process.env.REACT_APP_APP_ORIGIN}/portfolio`;
    } else {
        return `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://app.${process.env.REACT_APP_APP_ORIGIN}/o/${organization.tag}`;
    }
};

export const Dashboard = () => {
    const navigate = useNavigate();
    const { userContext, setUserContext } = useUserContext();
    const { brandContext } = useBrandContext();
    const { subdomain, subdomainFetched } = useSubdomain();
    const invitationCode = localStorage.getItem("invcode");
    const location = useLocation();
    const { advisorUuid, touchpointUuid } = useParams();

    useEffect(() => {
        debugLog('[Dashboard] Starting redirection logic', {
            hasInvitationCode: !!invitationCode,
            subdomain,
            brandTag: brandContext?.brand?.tag,
            userFetched: userContext.fetched,
            hasUser: !!userContext.dbUser,
            userRole: userContext.dbUser?.role,
            projectMembershipsCount: userContext.projectMemberships?.length,
            organizationMembershipsCount: userContext.organizationMemberships?.length
        });

        if (invitationCode && invitationCode !== "") {
            debugLog('[Dashboard] Would redirect to invitation acceptance', { 
                action: 'navigate',
                path: `/invitation/${invitationCode}/accept`
            });
            return navigate(`/invitation/${invitationCode}/accept`);
        }

        if (!userContext.fetched || !userContext.dbUser) {
            debugLog('[Dashboard] User context not ready or no user found');
            return;
        }

        const { projectMemberships, organizationMemberships } = userContext;
        const searchTag = subdomain || brandContext?.brand?.tag;

        debugLog('[Dashboard] Search parameters', { 
            searchTag,
            usingSubdomain: !!subdomain,
            usingBrandTag: !!brandContext?.brand?.tag
        });

        // Only try exact matches if we have a search tag
        if (searchTag) {
            // 1. Check for exact match in project memberships
            const matchingProjectMembership = findProjectInMemberships(projectMemberships, searchTag);
            if (matchingProjectMembership) {
                debugLog('[Dashboard] Would redirect to project from user memberships', {
                    action: 'window.location.href',
                    url: getProjectUrl(matchingProjectMembership.project, matchingProjectMembership, advisorUuid),
                    projectTag: matchingProjectMembership.project?.tag,
                    userRole: matchingProjectMembership.role
                });
                window.location.href = getProjectUrl(matchingProjectMembership.project, matchingProjectMembership, advisorUuid);
                return;
            }

            // 2. Check for project in organization memberships
            const projectInOrg = findProjectInOrganizations(organizationMemberships, searchTag);
            if (projectInOrg) {
                debugLog('[Dashboard] Would redirect to project from organizations', {
                    action: 'window.location.href',
                    url: getProjectUrl(projectInOrg.project, null, advisorUuid),
                    projectTag: projectInOrg.project?.tag,
                    organizationTag: projectInOrg.organization?.tag
                });
                window.location.href = getProjectUrl(projectInOrg.project, null, advisorUuid);
                return;
            }

            // 3. Check for organization match
            const matchingOrganization = findOrganizationByTag(organizationMemberships, searchTag);
            if (matchingOrganization) {
                debugLog('[Dashboard] Would redirect to organization', {
                    action: 'window.location.href',
                    url: getOrganizationUrl(matchingOrganization),
                    organizationTag: matchingOrganization.tag,
                    experience: matchingOrganization.experience
                });
                window.location.href = getOrganizationUrl(matchingOrganization);
                return;
            }

            debugLog('[Dashboard] No exact matches found for search tag, proceeding with fallback logic');
        } else {
            debugLog('[Dashboard] No search tag available, proceeding with fallback logic');
        }

        // 4. Check if user is admin and redirect to admin
        if (userContext.dbUser.role === "admin") {
            debugLog('[Dashboard] Would redirect to admin dashboard', {
                action: 'navigate',
                path: '/admin'
            });
            navigate('/admin');
            return;
        }

        // 5. Redirect to first organization if exists
        if (organizationMemberships?.length > 0) {
            const firstOrg = organizationMemberships[0].organization;
            debugLog('[Dashboard] Would redirect to first organization', {
                action: 'window.location.href',
                url: getOrganizationUrl(firstOrg),
                organizationTag: firstOrg.tag,
                experience: firstOrg.experience
            });
            window.location.href = getOrganizationUrl(firstOrg);
            return;
        }

        // 6. Redirect to first project if exists
        if (projectMemberships?.length > 0) {
            const firstProject = projectMemberships[0];
            debugLog('[Dashboard] Would redirect to first project', {
                action: 'window.location.href',
                url: getProjectUrl(firstProject.project, firstProject, advisorUuid),
                projectTag: firstProject.project?.tag,
                userRole: firstProject.role
            });
            window.location.href = getProjectUrl(firstProject.project, firstProject, advisorUuid);
            return;
        }

        // If no matches and no memberships, show not available
        if (searchTag !== "app") {
            debugLog('[Dashboard] Would redirect to Auth0 Callback page', {
                action: 'window.location.href',
                url: `${process.env.REACT_APP_AUTH0_CALLBACK_URL}`
            });
            window.location.href = `${process.env.REACT_APP_AUTH0_CALLBACK_URL}`;
            return;
        }

        debugLog('[Dashboard] Would show not-available page', {
            action: 'navigate',
            path: '/not-available',
            replace: true
        });
        navigate('/not-available', { replace: true });

    }, [userContext.fetched, brandContext.fetched, subdomainFetched]);

    if (!userContext.fetched) return <TransitionOverlay open={true} message="Loading..." />;
    if (userContext.fetched && !userContext.dbUser) return <UserNotFound />;
    return <></>;
}